import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { SkeletonArchetype } from '../../../core/services/navigation-busy.service';

/**
 * The route-transition skeleton. All ~60 routes in this app are `loadComponent`
 * lazy chunks, so between clicking a sidebar item and the component appearing the
 * <main> region was simply empty — a white gap under a still-painted header, which
 * reads as a crash rather than as loading.
 *
 * It draws the shell honestly and the body approximately, which is the trade this
 * layer exists to make. The header — its 44px icon box, title and subtitle, actions
 * row — renders on every route, so reserving it is a promise this component can
 * keep. The list-panel column renders only where the incoming route declares
 * `data: { listPanel: true }`, but wherever it does appear its geometry is fixed,
 * so that promise holds too. The body is one of five coarse
 * archetypes declared per route in app.routes.ts, so a `table` route whose real
 * table has three columns will still settle slightly. Pixel-exact bodies for 60
 * routes would mean 60 files mirroring templates they do not import, and the five
 * hand-rolled skeletons this work replaced are the evidence for how that ends.
 *
 * Pages people look at most (orders, dashboard) get box-for-box treatment in their
 * own components instead — see the page layer.
 */
@Component({
  selector: 'app-page-skeleton',
  standalone: true,
  imports: [SkeletonComponent, TranslateModule],
  templateUrl: './page-skeleton.component.html',
  styleUrls: ['./page-skeleton.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageSkeletonComponent {
  @Input() archetype: SkeletonArchetype = 'table';

  /** Whether the incoming route renders a master-detail list panel. The skeleton
   *  cannot infer this — `mode` lives inside the component that has not loaded —
   *  so it is declared per route in app.routes.ts. */
  @Input() listPanel = false;

  /** Fixed-length arrays for @for. Angular's @for needs an iterable; these are
   *  cheaper and clearer than a pipe or a getter that rebuilds each cycle. */
  readonly two = [0, 1];
  readonly four = [0, 1, 2, 3];
  readonly five = [0, 1, 2, 3, 4];
  readonly six = [0, 1, 2, 3, 4, 5];
  readonly eight = [0, 1, 2, 3, 4, 5, 6, 7];
}
